
import type { LoaderFunctionArgs } from 'react-router-dom';
import { Form, redirect, useActionData } from 'react-router-dom';
import { Button, Input } from 'semantic-ui-react';
import { gql } from '@apollo/client';

import { authProvider } from '../../auth';
import { PATHS } from '../../paths';
import { client } from '../../apolloClient';
import { AuthCard } from '../AuthCard';

import formStyles from '../AuthCard/form.module.css';

const SIGNUP_MUTATION = gql`
    mutation CreateUser($code: String!, $userName: String!, $password: String!) {
        createUser(code: $code, userName: $userName, password: $password) {
            token
            refreshToken
        }
    }
`;

function SignUpPage() {
    const actionData = useActionData() as { error: string } | undefined;

    return (
        <AuthCard title="Sign up">
            <Form method="post" replace>
                <div className={formStyles.field}>
                    <Input
                        fluid
                        icon='user'
                        iconPosition='left'
                        placeholder='Username'
                        name='username'
                    />
                </div>
                <div className={formStyles.field}>
                    <Input
                        fluid
                        icon='lock'
                        iconPosition='left'
                        placeholder='Password'
                        name='password'
                        type='password'
                    />
                </div>
                <div className={formStyles.field}>
                    <Input
                        fluid
                        icon='key'
                        iconPosition='left'
                        placeholder='Organisation code'
                        name='code'
                    />
                </div>
                {actionData?.error && (
                    <p className={formStyles.errorMsg}>{actionData.error}</p>
                )}
                <Button
                    type="submit"
                    fluid
                    size="large"
                    className={formStyles.submitBtn}
                >
                    Sign up
                </Button>
            </Form>
            <p className={formStyles.switchLink}>
                Already have an account? <a href={PATHS.LOGIN}>Log in</a>
            </p>
        </AuthCard>
    );
}

async function signupAction({ request }: LoaderFunctionArgs) {
    const formData = await request.formData();
    const code = formData.get('code') as string | null;
    const username = formData.get('username') as string | null;
    const password = formData.get('password') as string | null;

    if (!username) {
        return { error: 'Please enter a username.' };
    }
    if (!code || code.length < 1) {
        return { error: 'You need an organisation code to sign up.' };
    }
    if (!password || password.length < 10) {
        return { error: 'Password must be at least 10 characters.' };
    }

    try {
        const result = await client.mutate({
            mutation: SIGNUP_MUTATION,
            variables: { userName: username, password, code },
        });
        if (result.errors) throw new Error();
        if (result?.data?.createUser?.token) {
            await authProvider.signin(result.data.createUser.token, result.data.createUser.refreshToken);
        }
    } catch {
        return { error: 'Invalid sign-up attempt. Check your organisation code and try again.' };
    }

    // See Login's loginAction — same reset is needed here so Layout's GET_ME guard
    // doesn't bounce the new user back to /organisations on their way to onboarding.
    await client.resetStore();

    return redirect(PATHS.ONBOARDING);
}

async function signupLoader() {
    if (authProvider.isAuthenticated()) {
        return redirect(PATHS.ORGANISATIONS);
    }
    return null;
}

export { SignUpPage, signupAction, signupLoader };
